import { useEffect, type ReactNode } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/store';
import { uiSocket } from '../live/socket';
import { installMedia, resetMedia } from '../live/media';
import { useLive } from '../live/store';
import { useT } from '../i18n';
import { stopRobots } from '../api/queries';
import { perRobotToast, toast, toastErr } from './toast';
import { IconAgent, IconAutonomy, IconCalendar, IconDrive, IconFleet, IconLogout, IconMap, IconMedia, IconUpdates } from './Icon';
import { HandPushFab } from './HandPushFab';

export function RequireAuth() {
  const auth = useAuth((s) => s.auth);
  const loc = useLocation();
  const qc = useQueryClient();
  useEffect(() => {
    if (!auth) return;
    installMedia();
    uiSocket.start();
    return () => {
      uiSocket.stop();
      resetMedia();
      qc.clear();
    };
  }, [auth, qc]);
  if (!auth) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return <Outlet />;
}

export function Shell() {
  const t = useT();
  const ws = useLive((s) => s.wsState);
  const setAuth = useAuth((s) => s.setAuth);
  const items = [
    { to: '/fleet', label: t('nav.fleet'), icon: <IconFleet /> },
    { to: '/maps', label: t('nav.maps'), icon: <IconMap /> },
    { to: '/schedule', label: t('nav.schedule'), icon: <IconCalendar /> },
    { to: '/autonomy', label: t('nav.autonomy'), icon: <IconAutonomy /> },
    { to: '/media', label: t('nav.media'), icon: <IconMedia /> },
    { to: '/drive', label: t('nav.drive'), icon: <IconDrive /> },
    { to: '/agent', label: t('nav.agent'), icon: <IconAgent /> },
    { to: '/updates', label: t('nav.updates'), icon: <IconUpdates /> },
  ];
  const loc = useLocation();
  return (
    <div className="app">
      <nav className="rail" aria-label={t('nav.main')}>
        <div className="logo" aria-hidden>
          C
        </div>
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            className={({ isActive }) =>
              `rail-link${isActive || (it.to === '/drive' && /^\/(robots|drive)\//.test(loc.pathname)) ? ' active' : ''}`
            }
          >
            {it.icon}
            {it.label}
          </NavLink>
        ))}
        <div className="rail-spacer" />
        <div className="rail-status">
          <span className={`conn ${ws}`} title={t(`ws.${ws}`)} role="img" aria-label={t(`ws.${ws}`)} />
          <button type="button" className="rail-btn" title={t('nav.logout')} aria-label={t('nav.logout')} onClick={() => setAuth(null)}>
            <IconLogout />
          </button>
        </div>
      </nav>
      <div className="main">
        {ws === 'closed' && (
          <div className="reconnect-banner" role="status">
            <span className="conn closed" /> {t('ws.reconnecting')}
          </div>
        )}
        <Outlet />
      </div>
      <HandPushFab />
    </div>
  );
}

export function StopAllButton({ compact }: { compact?: boolean }) {
  const t = useT();
  const onClick = async () => {
    const online = Object.values(useLive.getState().robots)
      .filter((r) => r.online)
      .map((r) => r.serial);
    if (!online.length) return toast(t('stop.noneOnline'), 'err');
    try {
      const r = await stopRobots(online);
      perRobotToast(t('stop.all'), r.results);
    } catch (e) {
      toastErr(e);
    }
  };
  return (
    <button type="button" className="stop-all" onClick={onClick} title={t('stop.allTitle')}>
      {compact ? t('stop.short') : t('stop.all')}
    </button>
  );
}

export function PageHeader({ title, children, stopAll = true }: { title: ReactNode; children?: ReactNode; stopAll?: boolean }) {
  return (
    <header className="page-header">
      {typeof title === 'string' ? <h1 className="page-title">{title}</h1> : title}
      {children}
      {stopAll && <StopAllButton />}
    </header>
  );
}
